import { useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  flexRender,
  createColumnHelper,
  type Row,
} from '@tanstack/react-table';
import type { AuditEntry, PaginationInfo } from '../api/types';
import { SHELVE_LABELS } from '../api/types';
import { useAuditLog } from '../hooks/useAuditLog';

function summarize(entry: AuditEntry): string {
  try {
    const newData = entry.newData ? JSON.parse(entry.newData) : null;
    const oldData = entry.oldData ? JSON.parse(entry.oldData) : null;
    const source = newData || oldData;
    if (!source) return '-';

    const carrier = source.carrierCode || '?';
    const fare = source.fareFamily || '?';
    const label = (s: string) => SHELVE_LABELS[s] || s;

    if (entry.action === 'TOKEN_CREATE' && newData) {
      return `Created token "${newData.name}" (${newData.role}) for ${newData.email}`;
    }
    if (entry.action === 'TOKEN_REVOKE' && oldData) {
      return `Revoked token "${oldData.name}" (${oldData.role}) for ${oldData.email}`;
    }
    if (entry.action === 'CREATE') {
      return `${carrier}/${fare}: ${label(newData.correctShelve)}`;
    }
    if (entry.action === 'DELETE') {
      return `${carrier}/${fare}: removed`;
    }
    if ((entry.action === 'UPDATE' || entry.action === 'PATCH') && oldData && newData) {
      if (oldData.correctShelve !== newData.correctShelve) {
        return `${carrier}/${fare}: ${label(oldData.correctShelve)} -> ${label(newData.correctShelve)}`;
      }
      return `${carrier}/${fare}: fields updated`;
    }
  } catch {
    // ignore parse errors
  }
  return '-';
}

const actionColors: Record<string, string> = {
  CREATE: 'bg-green-100 text-green-800',
  UPDATE: 'bg-blue-100 text-blue-800',
  PATCH: 'bg-yellow-100 text-yellow-800',
  DELETE: 'bg-red-100 text-red-800',
  TOKEN_CREATE: 'bg-purple-100 text-purple-800',
  TOKEN_REVOKE: 'bg-orange-100 text-orange-800',
};

const col = createColumnHelper<AuditEntry>();

const columns = [
  col.display({
    id: 'expand',
    size: 30,
    cell: ({ row }) => (
      <button
        onClick={row.getToggleExpandedHandler()}
        className="text-gray-500 hover:text-gray-800 text-xs"
      >
        {row.getIsExpanded() ? '[-]' : '[+]'}
      </button>
    ),
  }),
  col.accessor('createdAt', {
    header: 'Timestamp',
    size: 170,
    cell: (info) => {
      const v = info.getValue();
      try { return new Date(v).toLocaleString(); } catch { return v; }
    },
  }),
  col.accessor('action', {
    header: 'Action',
    size: 80,
    cell: (info) => {
      const v = info.getValue();
      return (
        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${actionColors[v] || ''}`}>
          {v}
        </span>
      );
    },
  }),
  col.accessor('overrideId', {
    header: 'Override ID',
    size: 90,
    cell: (info) => info.getValue() ?? '-',
  }),
  col.accessor('userEmail', {
    header: 'User',
    size: 180,
    cell: (info) => info.getValue() || '-',
  }),
  col.accessor('sourceDomain', {
    header: 'Source',
    size: 150,
    cell: (info) => info.getValue() || '-',
  }),
  col.display({
    id: 'summary',
    header: 'Summary',
    size: 250,
    cell: ({ row }) => summarize(row.original),
  }),
];

function DetailRow({ row }: { row: Row<AuditEntry> }) {
  const entry = row.original;
  let oldData: any = null;
  let newData: any = null;
  try { oldData = entry.oldData ? JSON.parse(entry.oldData) : null; } catch {}
  try { newData = entry.newData ? JSON.parse(entry.newData) : null; } catch {}

  return (
    <tr>
      <td colSpan={columns.length} className="px-4 py-3 bg-gray-50 border-t border-gray-100">
        <div className="flex gap-6 text-xs font-mono">
          {oldData && (
            <div className="flex-1">
              <div className="font-semibold text-gray-600 mb-1">Before</div>
              <pre className="whitespace-pre-wrap bg-white p-2 rounded border border-gray-200 max-h-48 overflow-auto">
                {JSON.stringify(oldData, null, 2)}
              </pre>
            </div>
          )}
          {newData && (
            <div className="flex-1">
              <div className="font-semibold text-gray-600 mb-1">After</div>
              <pre className="whitespace-pre-wrap bg-white p-2 rounded border border-gray-200 max-h-48 overflow-auto">
                {JSON.stringify(newData, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

export function AuditLog() {
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');

  const { data, pagination, loading } = useAuditLog({
    page,
    per_page: 50,
    action: actionFilter || undefined,
    user_email: emailFilter || undefined,
  });

  const table = useReactTable({
    data,
    columns,
    state: { expanded: {} },
    getRowCanExpand: () => true,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    manualPagination: true,
    pageCount: pagination.total_pages,
  });

  return (
    <div>
      {/* Filters */}
      <div className="flex gap-3 mb-3 items-end">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Action</label>
          <select
            value={actionFilter}
            onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
            className="text-sm border border-gray-300 rounded px-2 py-1.5"
          >
            <option value="">All</option>
            <option value="CREATE">CREATE</option>
            <option value="UPDATE">UPDATE</option>
            <option value="PATCH">PATCH</option>
            <option value="DELETE">DELETE</option>
            <option value="TOKEN_CREATE">TOKEN_CREATE</option>
            <option value="TOKEN_REVOKE">TOKEN_REVOKE</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">User Email</label>
          <input
            type="text"
            value={emailFilter}
            onChange={(e) => { setEmailFilter(e.target.value); setPage(1); }}
            placeholder="filter by email"
            className="text-sm border border-gray-300 rounded px-2 py-1.5 w-56"
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-gray-100">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-3 py-2 text-left font-medium text-gray-700 whitespace-nowrap"
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-8 text-gray-400">
                  Loading...
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-8 text-gray-400">
                  No audit entries found
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <>
                  <tr key={row.id} className="border-t border-gray-100 hover:bg-blue-50">
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-3 py-1.5 whitespace-nowrap">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                  {row.getIsExpanded() && <DetailRow key={`${row.id}-detail`} row={row} />}
                </>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination pagination={pagination} onPageChange={setPage} />
    </div>
  );
}

function Pagination({
  pagination,
  onPageChange,
}: {
  pagination: PaginationInfo;
  onPageChange: (p: number) => void;
}) {
  if (pagination.total === 0) return null;
  return (
    <div className="flex items-center justify-between mt-3 text-sm text-gray-600">
      <span>
        Showing {(pagination.page - 1) * pagination.per_page + 1}-
        {Math.min(pagination.page * pagination.per_page, pagination.total)} of{' '}
        {pagination.total} entries
      </span>
      <div className="flex gap-1">
        <button
          disabled={pagination.page <= 1}
          onClick={() => onPageChange(pagination.page - 1)}
          className="px-2 py-1 border rounded disabled:opacity-30"
        >
          Prev
        </button>
        <button
          disabled={pagination.page >= pagination.total_pages}
          onClick={() => onPageChange(pagination.page + 1)}
          className="px-2 py-1 border rounded disabled:opacity-30"
        >
          Next
        </button>
      </div>
    </div>
  );
}
